import { ChangeEvent, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { SongSlug } from '../../../../common/src/model.ts';
import stopwatchIcon from '../../assets/stopwatch.png';
import { setSongTempoForOrg, useCurrentOrg } from '../../data/use_current_org.tsx';
import { useUserAttributes } from '../../data/use_user_attributes.ts';
import { QuarterNote } from '../../util/shared.tsx';
import { useEffectOnce } from '../../util/use_effect_once.ts';
import {
  SliderBase,
  SliderButtonAndRangeWrapper,
  SliderButtonWrapper,
  SliderLabel,
  VoiceButtonWrapper,
} from '../player_util.tsx';
import { SequencerContext } from '../sequencer.tsx';

const TempoLabelButtonHeight = '60px';
const sliderMin = -4;
const sliderMax = 4;

// The tempo slider changes very slowly in the middle and more rapidly toward the ends
// At either extreme, the value animates up/down to allow arbitrarily high/low values
export function TempoSlider({ songSlug }: { songSlug?: SongSlug }) {
  const {
    defaultTempo,
    currentTempo,
    readyToPlay,
    setTempoCenterShift,
    setTempoMinorAdjustment,
    onTempoChange,
    clearUserSpecifiedTempo,
    registerClearTempoListener,
    unregisterClearTempoListener,
  } = use(SequencerContext);
  const { currentOrg, updateOrgInfo } = useCurrentOrg();
  const { isInternalUser, isOrgAdmin } = useUserAttributes();
  const state: { timer?: ReturnType<typeof setTimeout> } = useMemo(() => ({}), []);
  const sliderInputRef = useRef<HTMLInputElement>(null);
  const [isPointerDown, setIsPointerDown] = useState(false);
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (isPointerDown && (value === sliderMin || value === sliderMax)) {
      if (!state.timer) {
        // we are using a global variable here to shut off the timer as quickly as possible later

        state.timer = setInterval(() => {
          setTempoCenterShift((currentOffset) => currentOffset + (value > 0 ? 1 : -1));
        }, 100);
      }
    } else {
      if (state.timer) {
        clearInterval(state.timer);
        state.timer = undefined;
      }
    }
  }, [isPointerDown, setTempoCenterShift, setTempoMinorAdjustment, state, value]);

  const onTempoSliderChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = Number(event.target.value);
      setValue(newValue);
      setTempoMinorAdjustment(newValue);
    },
    [setTempoMinorAdjustment],
  );

  useEffectOnce(() => {
    return () => {
      if (state.timer !== undefined) clearInterval(state.timer);
    };
  });

  useEffect(() => {
    const listener = () => {
      if (sliderInputRef.current) {
        sliderInputRef.current.value = '0';
      }
    };
    registerClearTempoListener(listener);
    return () => unregisterClearTempoListener(listener);
  }, [registerClearTempoListener, unregisterClearTempoListener]);

  const onPointerDown = useCallback(() => {
    setIsPointerDown(true);
  }, []);

  const onPointerUp = useCallback(() => {
    setIsPointerDown(false);
    onTempoChange();
  }, [onTempoChange]);

  const onSetOrganizationTempo = useCallback(async () => {
    const songTempo = currentTempo;
    if (!currentOrg || !songSlug || !songTempo) {
      return;
    }
    clearUserSpecifiedTempo();
    await updateOrgInfo(setSongTempoForOrg({ org: currentOrg, songSlug, songTempo }));
  }, [currentOrg, currentTempo, clearUserSpecifiedTempo, songSlug, updateOrgInfo]);

  const displayTempoButton =
    readyToPlay && currentTempo && currentTempo !== defaultTempo && (isInternalUser || isOrgAdmin);
  const tempoWidth = currentTempo && currentTempo >= 100 ? '3ch' : '2ch';

  return (
    <SliderButtonAndRangeWrapper>
      <SliderButtonWrapper>
        {displayTempoButton ? (
          <TempoButton onClick={onSetOrganizationTempo}>
            <div>Set to</div>
            <div>{currentTempo}</div>
          </TempoButton>
        ) : (
          <TempoLabel>
            <QuarterNote hidden={!currentTempo} />
            <SliderLabel hidden={!currentTempo}>=</SliderLabel>
            <SliderLabel key="tempo-output" style={{ width: tempoWidth }}>
              {currentTempo ?? '-'}
            </SliderLabel>
          </TempoLabel>
        )}
      </SliderButtonWrapper>
      <TempoSliderWrapper
        ref={sliderInputRef}
        defaultValue={0}
        min={sliderMin}
        max={sliderMax}
        step={0.01}
        onChange={onTempoSliderChange}
        onPointerUp={onPointerUp}
        onPointerDown={onPointerDown}
      />
    </SliderButtonAndRangeWrapper>
  );
}

const TempoLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: right;
  gap: 6px;
  height: ${TempoLabelButtonHeight};
`;

const TempoButton = styled(VoiceButtonWrapper)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: ${TempoLabelButtonHeight};
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 1.2rem;
`;

// the stopwatch thumb, shared with the practice video speed slider (GitHub issue #4041)
export const stopwatchThumbStyles = css`
  &::-webkit-slider-thumb {
    /*box-shadow: 1px 1px 1px #000000, 0 0 1px #0d0d0d;*/
    border: 2px solid var(--color-slider-thumb-border);
    height: 48px;
    width: 48px;
    border-radius: 10px;
    cursor: pointer;
    -webkit-appearance: none;
    margin-top: -20px;
    background-size: 25px;
    background-repeat: no-repeat;
    background-position: center;
    background-image: url(${stopwatchIcon});
    background-color: var(--color-slider-thumb);
  }
`;

const TempoSliderWrapper = styled(SliderBase)`
  ${stopwatchThumbStyles}
`;
